import React from "react";
import { connect } from "react-redux";
import { Alert, Button, Input, Select } from "antd";
import "antd/dist/antd.css";
import {
    dynamicAssetSearch,
    findAsset,
    getNearbyAssets,
    selectAsset,
    selectMarker,
    setAssetSearchType,
    setZoomEndCallback,
    toggleAssetDisplay,
    updateMapZoomAsyncWithCallback,
} from "redux/actions/index";
import _ from "lodash";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import AssetIDHelper from "components/util/AssetIDHelper";
import { SearchOutlined } from "@ant-design/icons";

class FindAssetForm extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            searchString: "",
            searchOptions: undefined,
            nameInput: "",
            assetTypeSelect: {},
            errorText: "",
            currentMinZoomLevel: 13,
            settingZoom: false,
        };

        this.updateDynamicSearchDebounced = _.debounce(this.updateDynamicSearch, 500);
    }

    componentDidMount = () => {
        if (this.props.assets.assetTypes.length) {
            this.setState({
                assetTypeSelect: this.props.assets.assetTypes[0],
            });
        }
    };

    onInputChange = (e) => {
        this.setState({
            nameInput: e.target.value,
        });
    };

    componentDidUpdate = (prevProps, prevState) => {
        if (!prevProps.assets.assetTypes.length && this.props.assets.assetTypes.length) {
            this.setState({
                assetTypeSelect: this.props.assets.assetTypes[0],
            });
        }

        if (this.state.settingZoom === false && prevState.settingZoom === true) {
            this.props.dispatch(
                setZoomEndCallback(() => {
                    this.props.dispatch(getNearbyAssets(this.state.assetTypeSelect.id, this.state.assetTypeSelect.ml, this.nearbySearchCallback));
                    this.props.dispatch(setZoomEndCallback(null));
                }),
            );
        }
    };

    searchForAsset = () => {
        const _this = this;
        const isSearchForMLAsset = this.state.assetTypeSelect.ml;
        let formatName;
        if (isSearchForMLAsset) {
            // ML assets all use CSV format, convert input to the same format
            formatName = new AssetIDHelper(_this.state.nameInput).format_search_query();
        } else {
            // imported asset data uses original format, search must match
            formatName = _this.state.nameInput;
        }

        this.props.dispatch(
            findAsset(this.state.assetTypeSelect.id, formatName, this.state.assetTypeSelect.ml, false, function (assets) {
                if (assets && assets.length) {
                    _this.props.dispatch(toggleAssetDisplay());
                    _this.props.dispatch(setAssetSearchType({ searchType: "specific" }));
                    _this.props.closeModal();
                } else {
                    _this.setState({
                        errorText: "No matching assets found",
                    });
                }
            }),
        );
    };
    updateDynamicSearch = (searchString) => {
        this.props.dispatch(
            dynamicAssetSearch(searchString, [this.state.assetTypeSelect.name], (searchOptions) => {
                if (this.state.searchString === searchString) {
                    this.setState({ searchOptions });
                }
            }),
        );
    };

    nearbySearchCallback = (assets) => {
        if (assets && assets.length) {
            this.props.dispatch(toggleAssetDisplay());
            this.props.dispatch(setAssetSearchType({ searchType: "nearby" }));
            this.props.closeModal();
        } else {
            this.setState({
                errorText: "No assets found in area",
            });
        }
    };

    searchCallback = (assets, mlSearch = false) => {
        if (assets && assets.length) {
            if (mlSearch && assets.length === 1) {
                this.props.dispatch(selectAsset(assets[0].id));
                this.props.dispatch(selectMarker(assets[0]));
            }

            this.props.dispatch(toggleAssetDisplay());
            this.props.dispatch(setAssetSearchType({ searchType: "specific" }));
            this.props.closeModal();
        } else {
            this.setState({
                errorText: "No matching asset(s) found",
            });
        }
    };

    searchForMLAsset = () => {
        this.setState({ errorText: "" });
        const formattedName = new AssetIDHelper(this.state.nameInput).format_search_query();
        this.props.dispatch(
            findAsset(this.state.assetTypeSelect.id, formattedName, true, (assets) => {
                this.searchCallback(assets, true);
            }),
        );
    };

    searchForImportedAsset = (asset_type_id, asset_id) => {
        this.setState({ errorText: "" });
        this.props.dispatch(findAsset(asset_type_id, asset_id, false, this.searchCallback));
    };

    findNearby = () => {
        this.setState({ errorText: "" });

        if (this.props.mapBounds.zoom < this.state.currentMinZoomLevel) {
            this.setState({ settingZoom: true });

            this.props.dispatch(
                updateMapZoomAsyncWithCallback(this.state.currentMinZoomLevel, () => {
                    setTimeout(() => this.setState({ settingZoom: false }));
                }),
            );
        } else {
            this.props.dispatch(getNearbyAssets(this.state.assetTypeSelect.id, this.state.assetTypeSelect.ml, this.nearbySearchCallback));
        }
    };

    renderSelectOptions = () => {
        const aivrOptions = [];
        const externalOptions = [];

        this.props.assets.assetTypes.forEach((type) => {
            if (type.ml) {
                aivrOptions.push(
                    <Select.Option
                        value={`${type.id}-${type.name}`}
                        key={`${type.id}-${type.name}`}
                        asset={type}
                        min_zoom_level={type.min_zoom_level}>
                        {type.name}
                    </Select.Option>,
                );
            } else {
                externalOptions.push(
                    <Select.Option
                        value={`${type.id}-${type.name}`}
                        key={`${type.id}-${type.name}`}
                        asset={type}
                        min_zoom_level={type.min_zoom_level}>
                        {type.name}
                    </Select.Option>,
                );
            }
        });

        return (
            <Select
                onSelect={this.selectAssetType}
                className="AssetTypeSelect"
                value={`${this.state.assetTypeSelect.id}-${this.state.assetTypeSelect.name}`}>
                <Select.OptGroup
                    value="aivr asset data"
                    label="AIVR Asset Data">
                    {aivrOptions}
                </Select.OptGroup>
                <Select.OptGroup
                    value="imported data"
                    label="Imported Data">
                    {externalOptions}
                </Select.OptGroup>
            </Select>
        );
    };

    selectAssetType = (_, obj) => {
        this.setState({
            assetTypeSelect: obj.props.asset,
            currentMinZoomLevel: obj.props.min_zoom_level ?? 13,
        });
    };

    renderSearchSelect = () => (
        <Select
            showSearch
            className="AssetSearchInput"
            value={this.state.nameInput || undefined}
            placeholder="Search assets..."
            defaultActiveFirstOption={false}
            suffixIcon={<SearchOutlined className="CustomSuffixIcon" />}
            filterOption={false}
            onSearch={(value) => {
                this.setState({ searchString: value });

                if (!value) {
                    this.setState({ searchOptions: undefined });
                    return;
                }

                this.updateDynamicSearchDebounced(value);
            }}
            onChange={(value, optionData) => {
                this.setState({ nameInput: optionData.props.asset.searchtext });
                this.searchForImportedAsset(optionData.props.asset.asset_type_id, value);
            }}
            onBlur={() => {
                this.setState({ searchOptions: undefined });
            }}
            notFoundContent={this.state.searchOptions ? "No results found" : null}>
            {this.state.searchOptions &&
                this.state.searchOptions.length &&
                this.state.searchOptions.map((item) => (
                    <Select.Option
                        className="AssetSearchInputOption"
                        value={item.asset_id}
                        key={item.asset_id}
                        asset={item}>
                        <div>
                            <span>{item.searchtext}</span>
                            <span>{item.asset_type}</span>
                        </div>
                    </Select.Option>
                ))}
        </Select>
    );

    render = () => {
        const assetInfoString = !this.state.assetTypeSelect || !this.state.assetTypeSelect.description ? null : `${this.state.assetTypeSelect.description}`;

        return (
            <div className="AssetSearchTab">
                <div className="Section">
                    {this.props.assets.assetTypes.length > 0 ? (
                        <div className="AssetTypeContainer">
                            <div className="AssetTypeSelectContainer">
                                {this.renderSelectOptions()}
                                <Tippy
                                    arrow={true}
                                    placement="right"
                                    zIndex={1100}
                                    delay={100}
                                    theme={"aivr"}
                                    content={
                                        <div className="AssetTypeTooltip">
                                            <p>
                                                AIVR Asset Data is constantly updated, using Machine Learning to identify assets which are matched against
                                                existing external records for verification.
                                            </p>
                                            <p>Imported data is data imported from any external source</p>
                                        </div>
                                    }>
                                    <div>
                                        <FontAwesomeIcon
                                            className="InfoIcon"
                                            icon={faInfoCircle}
                                        />
                                    </div>
                                </Tippy>
                            </div>
                            <Button
                                onClick={this.findNearby}
                                key="find"
                                type="primary">
                                Find Nearby
                            </Button>
                        </div>
                    ) : (
                        "No asset types configured for this workspace."
                    )}
                    {assetInfoString && (
                        <Alert
                            className="AssetTypeInfoString"
                            message={assetInfoString}
                            type="info"
                        />
                    )}
                </div>
                <div className="Section">
                    {this.state.assetTypeSelect.ml ? (
                        <form
                            className="AssetSearchForm"
                            onSubmit={(e) => {
                                e.preventDefault();
                                this.searchForMLAsset();
                            }}>
                            <Input
                                onChange={this.onInputChange}
                                className="AssetSearchInput"
                                placeholder="Asset Name"
                                value={this.state.nameInput}
                            />
                            {this.state.assetTypeSelect.ml && (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="assetButton"
                                    disabled={this.state.nameInput === ""}>
                                    Search for asset
                                </Button>
                            )}
                        </form>
                    ) : (
                        this.renderSearchSelect()
                    )}
                </div>
                {this.state.errorText && <span className="Error">{this.state.errorText}</span>}
            </div>
        );
    };
}

const mapStateToProps = (state) => {
    let currentDashboard = _.find(state.dashboards, (dash) => dash.access_id === state.userDetails.dashboardAccessID);

    return {
        assets: state.assets,
        mapBounds: state.sessionFilters.mapBounds,
        dashboardAssetTypes: currentDashboard.config.asset_types,
    };
};

export default connect(mapStateToProps)(FindAssetForm);
